import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import CallLog from "@/services/call-log";
import type { GetCallLog } from "@/dtos/CallLog.dto";

export interface CallLogsParams {
  organizationId: string;
  page?: number;
  limit?: number;
  createdById?: string;
  status?: string;
  contactId?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
  callDispositionId?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useCallLogs(params: Ref<CallLogsParams | null> | CallLogsParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["callLogs", "disabled"];
    return [
      "callLogs",
      p.organizationId,
      p.page || 1,
      p.limit || 25,
      p.createdById || "",
      p.status || "",
      p.contactId || "",
      p.startDate || "",
      p.endDate || "",
      p.userId || "",
      p.callDispositionId || "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetCallLog & Record<string, any> = {
        organizationId: p.organizationId,
        page: p.page || 1,
        limit: p.limit || 25,
      };

      if (p.createdById) queryParams.createdById = p.createdById;
      if (p.status) queryParams.status = p.status;
      if (p.contactId) queryParams.contactId = p.contactId;
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;
      if (p.userId) queryParams.userId = p.userId;
      if (p.callDispositionId) queryParams.callDispositionId = p.callDispositionId;

      const result = await CallLog.getCallLogs(queryParams);

      if (result.data.status) {
        return {
          data: result.data.data,
          metaData: result.data.metaData,
        };
      }

      throw new Error(result.data.msg || "Failed to load call logs");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
